import React, { useEffect, useMemo, useState } from 'react';
import theme from './OptionSigning.module.scss';
import { useHistory, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { configuratorKeys, getConfiguratorSignature, signConfigurator } from '@/services/configurator';
import { BuyerOptionCard } from '../BuyerOptionCard';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { ProductCardList } from '@/seller/components/products/ProductCard';
import { Accordion, Button, Card, Badge } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { groupBy, orderBy } from 'lodash-es';
import { t } from '@lingui/core/macro';
import { requestOneTimeCode } from '@/services/signatures';
import { getRoomDisplayName, GENERAL_ROOM_ID } from '../utils/configuratorRooms';
import {
    Banner,
    DateDistanceDisplay,
    MarkdownDisplay,
    Toast,
    ValidatingInput,
    SimpleWizard,
    SimpleWizardStep,
    SimpleWizardConfirmStep,
} from '@ygtechnologies/bygr-frontend-component-library';
import { useGetActiveProfile, useAppDispatch } from '@/utils/hooks';
import { loadProjectData } from '@/state/buyer/project-data/creator';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';

export interface OptionSigningProps {}

interface OptionSigningParams {
    clientKey: string;
}

enum Step {
    Summary = 1,
    Products = 2,
    ConfirmIdentity = 3,
    Done = 4,
}

const Steps = [Step.Summary, Step.Products, Step.ConfirmIdentity, Step.Done];

export const OptionSigning: React.FC<OptionSigningProps> = ({}) => {
    const profile = useGetActiveProfile();
    const [oneTimeCode, setOneTimeCode] = useState('');
    const [codeRequested, setCodeRequested] = useState(false);
    const [isRequestingCode, setIsRequestingCode] = useState(false);
    const [currentStep, setCurrentStep] = useState(Step.Summary);
    const { clientKey } = useParams<OptionSigningParams>();
    const { data: signatureData } = useQuery({
        queryKey: ['getConfiguratorSignature', clientKey],
        queryFn: () => getConfiguratorSignature(clientKey),
        enabled: !!clientKey,
    });
    const {
        setTabsEnabled,
        setBottomPanelEnabled,
        projectData,
        getRoomMeta,
        basePath,
        selectedDeadlineId,
        configuratorData,
        residentialUnitId,
    } = useOptionConfigurator();
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    const [isSigning, setIsSigning] = useState(false);
    const [signed, setSigned] = useState(false);
    const history = useHistory();
    const handleCancel = () => history.push(basePath);

    const currentDeadline = projectData?.deadlines.find((d) => d.id === selectedDeadlineId) || null;
    const optionTerms = projectData?.optionsTerms?.trim() ?? '';
    const requiresOptionTermsAcceptance = optionTerms.length > 0;

    useEffect(() => {
        setTabsEnabled(false);
        setBottomPanelEnabled(false);

        return () => {
            setTabsEnabled(true);
            setBottomPanelEnabled(true);
        };
    }, []);

    const rooms = useMemo(() => {
        if (!signatureData) {
            return null;
        }

        // The signature is its own snapshot, so derive rooms from its own groups and look up
        // each room's metadata by id (rather than enumerating every room in the unit).
        const groupsByRoom = groupBy(signatureData.optionGroups, (x) => x.roomId);

        const result = Object.entries(groupsByRoom)
            .filter(([roomId]) => roomId !== 'null')
            .map(([roomId, groups]) => {
                const id = Number(roomId);
                const meta = getRoomMeta(id);
                return {
                    room: { name: meta?.name ?? '', id, sortOrder: meta?.sortOrder ?? 0 },
                    groups,
                };
            });

        result.push({
            room: {
                id: GENERAL_ROOM_ID,
                name: getRoomDisplayName({ roomId: GENERAL_ROOM_ID, roomName: '' }),
                sortOrder: -1,
            },
            groups: signatureData.optionGroups.filter((x) => x.roomId === null),
        });

        return orderBy(
            result.filter((x) => x.groups?.length > 0),
            (x) => x.room.sortOrder
        );
    }, [signatureData, getRoomMeta]);

    const totalPrice = signatureData?.totalPrice ?? 0;

    const numberOfOptions = useMemo(() => {
        if (!rooms) return 0;
        return rooms.reduce((acc, room) => acc + room.groups.length, 0);
    }, [rooms]);

    const numberOfStyles = useMemo(() => {
        const pickedStylesFromConfigurator =
            configuratorData?.styleGroups.reduce(
                (acc, group) => acc + group.styles.filter((style) => style.isPicked).length,
                0
            ) ?? 0;

        if (pickedStylesFromConfigurator > 0) {
            return pickedStylesFromConfigurator;
        }

        if (!signatureData?.presetGroups) {
            return 0;
        }

        const pickedStyles = signatureData.presetGroups.reduce(
            (acc, group) => acc + group.styles.filter((style) => style.isPicked).length,
            0
        );

        // Some signing payloads include only signed preset groups without `isPicked` set.
        // In that case, each included group represents one selected style in the signature.
        if (pickedStyles === 0 && signatureData.presetGroups.length > 0) {
            return signatureData.presetGroups.length;
        }

        return pickedStyles;
    }, [configuratorData, signatureData]);

    const handleRequestCode = async () => {
        if (!signatureData) return;
        try {
            setIsRequestingCode(true);
            await requestOneTimeCode(signatureData.clientKey);
            setCodeRequested(true);
        } finally {
            setIsRequestingCode(false);
        }
    };

    const handleSign = async () => {
        if (!signatureData || !profile) {
            return;
        }

        try {
            setIsSigning(true);
            await signConfigurator(signatureData.clientKey, oneTimeCode, profile.id);
            setSigned(true);
            setCurrentStep(Step.Done);
            // After signing, the deadline's awaitingSignatures is stale until we refetch.
            // Invalidate the configurator query and reload projectData so the options page
            // and the sidebar unsigned-options dot reflect the signed state without a manual refresh.
            if (residentialUnitId != null) {
                await queryClient.invalidateQueries({
                    queryKey: configuratorKeys.byUnit(residentialUnitId),
                });
            }
            dispatch(loadProjectData(residentialUnitId));
        } catch (e: any) {
            if (e instanceof Response) {
                const response: Response = e;
                let message: string | null = null;

                try {
                    const bodyText = await response.text();
                    try {
                        const data = JSON.parse(bodyText);
                        message = (data && data.error) ?? null;
                    } catch {
                        message = bodyText.trim() || null;
                    }
                } catch {}

                if (response.status === HttpStatusCode.BadRequest) {
                    if (message === 'One time code is not correct') {
                        Toast.error(t`One time code is not correct`);
                    } else if (message === 'Token expired') {
                        Toast.error(t`Token expired`);
                    } else if (message === 'One or more of the items have changed') {
                        Toast.error(t`One or more of the items have changed`);
                    } else if (message === 'One or more of the preset selections have changed') {
                        Toast.error(t`One or more of the preset selections have changed`);
                    } else if (message === 'One or more of the option selections have changed') {
                        Toast.error(t`One or more of the option selections have changed`);
                    }
                } else {
                    Toast.error(t`An unexpected error occurred.`);
                }
            } else {
                Toast.error(t`An unexpected error occurred.`);
            }
        } finally {
            setIsSigning(false);
        }
    };

    if (!rooms || rooms.length === 0) {
        return (
            <div className={theme.emptyState}>
                <Trans>No options to sign.</Trans>
            </div>
        );
    }

    return (
        <div className={theme.container}>
            <SimpleWizard
                showProgressBar
                steps={Steps}
                currentStep={currentStep}
                onCancel={handleCancel}
                onStepChange={(step) => {
                    setCurrentStep(step);
                }}
                onSubmit={() => {}}
                onFinish={() => history.push(basePath)}
                finishButtonProps={{
                    children: <Trans>Back to options</Trans>,
                }}>
                <SimpleWizardStep
                    stepNumber={Step.Summary}
                    header={<Trans>Summary</Trans>}
                    navigationHeader={<Trans>Summary</Trans>}>
                    <Banner type="info">
                        <Trans>
                            You are about to sign and send your option selections. Please review the products on the next
                            step before confirming.
                        </Trans>
                    </Banner>
                    <Card>
                        {currentDeadline && (
                            <>
                                <div className={theme.infoRow}>
                                    <span>
                                        <Trans>Deadline</Trans>
                                    </span>
                                    <strong>{currentDeadline.name}</strong>
                                </div>
                                <div className={theme.infoRow}>
                                    <span>
                                        <Trans>Deadline expires</Trans>
                                    </span>
                                    <Badge appearance="subtle">
                                        <DateDistanceDisplay value={currentDeadline.deadline} />
                                    </Badge>
                                </div>
                            </>
                        )}
                        <div className={theme.infoRow}>
                            <span>
                                <Trans>Styles</Trans>
                            </span>
                            <strong>{numberOfStyles}</strong>
                        </div>
                        <div className={theme.infoRow}>
                            <span>
                                <Trans>Options</Trans>
                            </span>
                            <strong>{numberOfOptions}</strong>
                        </div>
                        <div className={theme.totalRow}>
                            <span>
                                <Trans>Total</Trans>
                            </span>
                            <strong>
                                <BuyerCurrencyDisplay value={totalPrice} />
                            </strong>
                        </div>
                    </Card>
                </SimpleWizardStep>

                <SimpleWizardStep
                    stepNumber={Step.Products}
                    header={<Trans>Review products</Trans>}
                    navigationHeader={<Trans>Products</Trans>}>
                    {rooms.map((room) => (
                        <Accordion
                            key={room.room.id}
                            isOpen={true}
                            title={
                                <div className={theme.accordionTitle}>
                                    <div>{room.room.name}</div>
                                    <div>
                                        <Badge appearance="subtle">
                                            <Trans>{room.groups.length} items</Trans>
                                        </Badge>
                                    </div>
                                </div>
                            }
                            className={theme.accordion}>
                            <ProductCardList>
                                {room.groups.map((g, idx) => (
                                    <BuyerOptionCard
                                        key={`${room.room.id}-${idx}`}
                                        option={g.selectedOption!}
                                        isSelected={true}
                                        groupName={g.name}
                                        hideSelectionSlot
                                    />
                                ))}
                            </ProductCardList>
                        </Accordion>
                    ))}
                    <div className={theme.totalRow}>
                        <strong>
                            <Trans>Total</Trans>
                        </strong>
                        <strong>
                            <BuyerCurrencyDisplay value={totalPrice} />
                        </strong>
                    </div>
                </SimpleWizardStep>

                <SimpleWizardStep
                    stepNumber={Step.ConfirmIdentity}
                    header={<Trans>Confirm identity</Trans>}
                    navigationHeader={<Trans>Confirm</Trans>}
                    hideButtons>
                    <Card>
                        <div className={theme.termsContainer}>
                            {requiresOptionTermsAcceptance && (
                                <>
                                    <h4>
                                        <Trans>Option terms</Trans>
                                    </h4>
                                    <MarkdownDisplay className={theme.termsText} markdown={optionTerms} />
                                </>
                            )}
                        </div>
                        <p>
                            <Trans>Enter the five digit SMS code to confirm your identity and sign your selections.</Trans>
                        </p>
                        <div className={theme.codeRow}>
                            <ValidatingInput
                                className="form-control"
                                value={oneTimeCode}
                                onChange={(e) => setOneTimeCode(e.target.value)}
                                placeholder={t`Enter SMS code here...`}
                                maxLength={5}
                            />
                            <Button variant="primary" onClick={handleRequestCode} loading={isRequestingCode}>
                                {codeRequested ? <Trans>Resend code</Trans> : <Trans>Get code</Trans>}
                            </Button>
                        </div>
                    </Card>
                    <div className={theme.signButtons}>
                        <Button variant="tertiary" onClick={handleCancel}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="tertiary" onClick={() => setCurrentStep(Step.Products)}>
                            <Trans>Back</Trans>
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSign}
                            loading={isSigning}
                            disabled={oneTimeCode.length !== 5 || isSigning}>
                            <Trans>Sign and send</Trans>
                        </Button>
                    </div>
                </SimpleWizardStep>

                <SimpleWizardConfirmStep
                    stepNumber={Step.Done}
                    header={<Trans>Thank you</Trans>}
                    navigationHeader={<Trans>Done</Trans>}>
                    <Card>
                        <h3>
                            <Trans>Your selections have been signed and sent!</Trans>
                        </h3>
                        <p>
                            <Trans>
                                Thank you for submitting your options. You can view your signed selections from the options
                                page.
                            </Trans>
                        </p>
                    </Card>
                </SimpleWizardConfirmStep>
            </SimpleWizard>
        </div>
    );
};
